import { useMemo } from 'react';

import {
	EVINCED_SEVERITIES,
	EVINCED_SEVERITIES_IDS,
	IFilterDefinition,
	OptionType
} from '@evinced-private/ui-common';

import {
	DEFAULT_ENVIRONMENT_DISPLAY_NAME,
	DEFAULT_UNIT_ID_DISPLAY_NAME,
	ENVIRONMENT_LABEL_KEY,
	UNIT_ID_LABEL_KEY
} from 'src/common/consts/platformConfigConsts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';

import { DASHBOARD_DATE_RANGE_OPTIONS } from '../../../constants/DashboardFiltersDefinitions';
import { processPlatformConfiguration } from '../../../helpers/ConfigurationProcessingHelpers';
import { createOptionsFromCoreLabels } from '../../../helpers/FilterOptionsHelpers';
import { getIssuesPageFilterDefinitions } from '../IssuesPageFilterDefinitions';

// Mock severity options - based on standard Evinced severities
const MOCK_SEVERITY_OPTIONS: OptionType[] = [
	{ value: EVINCED_SEVERITIES_IDS.CRITICAL, label: EVINCED_SEVERITIES.CRITICAL },
	{ value: EVINCED_SEVERITIES_IDS.SERIOUS, label: EVINCED_SEVERITIES.SERIOUS },
	{ value: EVINCED_SEVERITIES_IDS.MODERATE, label: EVINCED_SEVERITIES.MODERATE },
	{ value: EVINCED_SEVERITIES_IDS.MINOR, label: EVINCED_SEVERITIES.MINOR },
	{ value: EVINCED_SEVERITIES_IDS.BEST_PRACTICE, label: EVINCED_SEVERITIES.BEST_PRACTICE },
	{ value: EVINCED_SEVERITIES_IDS.NEEDS_REVIEW, label: EVINCED_SEVERITIES.NEEDS_REVIEW }
];

// Mock issue type options - common accessibility issue types
const MOCK_ISSUE_TYPE_OPTIONS: OptionType[] = [
	{ value: 'Accessible Name', label: 'Accessible Name' },
	{ value: 'Color Contrast', label: 'Color Contrast' },
	{ value: 'Keyboard Accessible', label: 'Keyboard Accessible' },
	{ value: 'Screen Reading Order', label: 'Screen Reading Order' },
	{ value: 'Tab Order', label: 'Tab Order' },
	{ value: 'Focus Indication', label: 'Focus Indication' },
	{ value: 'Missing Focus Indication', label: 'Missing Focus Indication' },
	{ value: 'Interactable Role', label: 'Interactable Role' },
	{ value: 'Missing Header Semantics', label: 'Missing Header Semantics' },
	{ value: 'Missing Footer Semantics', label: 'Missing Footer Semantics' },
	{ value: 'Not Meaningful Page Title', label: 'Not Meaningful Page Title' },
	{ value: 'Alt Text Is Too Long', label: 'Alt Text Is Too Long' },
	{ value: 'Text On Compound Background', label: 'Text On Compound Background' },
	{ value: 'Broken Skip links', label: 'Broken Skip links' },
	{ value: 'Relationship', label: 'Relationship' },
	{ value: 'Off-Screen Elements', label: 'Off-Screen Elements' }
];

export const useIssuesPageFilters = (): {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
} => {
	const { configuration } = useConfiguration();

	const { filterDefinitions, filterOptions } = useMemo(() => {
		const baseFilterOptions: Record<string, OptionType[]> = {
			dateRange: DASHBOARD_DATE_RANGE_OPTIONS,
			severities: MOCK_SEVERITY_OPTIONS,
			types: MOCK_ISSUE_TYPE_OPTIONS
		};

		if (!configuration?.PLATFORM) {
			return {
				filterDefinitions: getIssuesPageFilterDefinitions(),
				filterOptions: baseFilterOptions
			};
		}

		const { coreLabels, isValid } = processPlatformConfiguration(configuration.PLATFORM);

		if (!isValid) {
			return {
				filterDefinitions: getIssuesPageFilterDefinitions(),
				filterOptions: baseFilterOptions
			};
		}

		const environmentLabel = coreLabels.find((label) => label.label === ENVIRONMENT_LABEL_KEY);
		const unitIdLabel = coreLabels.find((label) => label.label === UNIT_ID_LABEL_KEY);

		const environmentDisplayName =
			environmentLabel?.displayName || DEFAULT_ENVIRONMENT_DISPLAY_NAME;
		const unitIdDisplayName = unitIdLabel?.displayName || DEFAULT_UNIT_ID_DISPLAY_NAME;

		// Use existing helper to create options from core labels
		const coreLabelOptions = createOptionsFromCoreLabels(
			coreLabels,
			'', // pathname not needed for issues page
			() => false // not on business units route
		);

		const filterDefinitions = getIssuesPageFilterDefinitions(
			unitIdDisplayName,
			environmentDisplayName
		);

		const filterOptions: Record<string, OptionType[]> = {
			...baseFilterOptions,
			...coreLabelOptions
		};

		return {
			filterDefinitions,
			filterOptions
		};
	}, [configuration]);

	return { filterDefinitions, filterOptions };
};
